import { Component, OnInit, inject, ChangeDetectionStrategy, Signal, input, computed } from '@angular/core';

import { CmsImageData } from '@fcom/core-api/interfaces/cms-interface';
import { AspectRatios, FetchPriorityEnum } from '@fcom/ui-components/components/images/enums';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking/booking-flow.interface';
import { ContentType } from '@fcom/common/interfaces/gtm.interface';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { LocationPair } from '@fcom/common/store/store.interface';
import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { PaxAmount } from '@fcom/dapi/interfaces/pax-details.interface';
import { isValidPaxAmount } from '@fcom/common/utils/pax-amount.utils';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

type AllowedAspectRatios = keyof typeof AspectRatios;

/**
 * Full Width Booking Widget
 *
 * @example
 * <cms-full-width-booking-widget
 *   [backgroundImage]="CmsImageData"
 *   [originCode]="'BER'"
 *   [destinationCode]="'HEL'"
 *   [enableAward]="true"
 *   [enableAurinkomatkat]="true"
 *   [enablePromoCode]="true"
 * ></cms-full-width-booking-widget>
 */
@Component({
  selector: 'cms-full-width-booking-widget',
  templateUrl: './cms-full-width-booking-widget.component.html',
  styleUrls: ['./cms-full-width-booking-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CmsFullWidthBookingWidgetComponent implements OnInit {
  protected readonly FetchPriorityEnum = FetchPriorityEnum;
  private mediaQueryService = inject(MediaQueryService);
  private monitoringLogger = inject(MonitoringLogger);
  private themeService = inject(FinnairThemeService);

  private readonly isMobile: Signal<boolean> = this.mediaQueryService.isBreakpoint('mobile');
  private readonly isTablet: Signal<boolean> = this.mediaQueryService.isBreakpoint('tablet');

  backgroundImage = input<CmsImageData>();
  contentType = input<ContentType>(ContentType.SINGLE_DESTINATION);
  enableAward = input<boolean>(false);
  enableAurinkomatkat = input<boolean>(false);
  enablePromoCode = input<boolean>(false);
  enableAutoFocus = input<boolean>(true);
  enableOpenJaw = input<boolean>(false);
  initialTripType = input<TripType>();
  initialTravelClass = input<GlobalBookingTravelClass>();
  initialPaxAmount = input<PaxAmount>();
  locations = input<BookingLocationCodes[]>([]);
  headingEnabled = input<boolean>(true);
  enableBackgroundImage = input<boolean>(true);
  enableFlightTab = input<boolean>(true);
  enableHotelsTab = input<boolean>(false);
  enableCarRentalTab = input<boolean>(false);
  disableOnDSelection = input<boolean>(false);

  readonly isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly aspectRatio = computed<AllowedAspectRatios>(() => {
    if (this.isMobile()) {
      return '1x1';
    }

    if (this.isTablet()) {
      return '4x3';
    }

    return '16x9';
  });

  readonly validInitialPaxAmount = computed<PaxAmount | undefined>(() => {
    const paxAmount = this.initialPaxAmount();
    return paxAmount && isValidPaxAmount(paxAmount) ? paxAmount : undefined;
  });

  defaultLocationPairs = computed<LocationPair[]>(() =>
    this.locations().map(
      (ond: BookingLocationCodes) =>
        ({
          origin: ond?.originLocationCode ? { locationCode: ond.originLocationCode } : undefined,
          destination: ond?.destinationLocationCode ? { locationCode: ond.destinationLocationCode } : undefined,
        }) as LocationPair
    )
  );

  ngOnInit(): void {
    if (this.initialTripType() && !Object.values(TripType).includes(this.initialTripType())) {
      this.monitoringLogger.error(
        `Unexpected initialTripType: ${this.initialTripType()} when rendering the booking widget in a content page`
      );
    }

    if (this.initialTravelClass() && !Object.values(GlobalBookingTravelClass).includes(this.initialTravelClass())) {
      this.monitoringLogger.error(
        `Unexpected initialTravelClass: ${this.initialTravelClass()} when rendering the booking widget in a content page`
      );
    }

    if (this.initialPaxAmount() && !this.validInitialPaxAmount()) {
      this.monitoringLogger.error(
        `Unexpected initialPaxAmount: ${JSON.stringify(this.initialPaxAmount())} when rendering the booking widget in a content page`
      );
    }
  }
}
